import { useCallback, useMemo, useState } from "react";
import styled from "@emotion/styled";
import type { SorterResult, TablePaginationConfig } from "antd5/es/table/interface";
import { capitalize } from "lodash";
import { Flex, Text } from "styles/utility-components";

import { DetailsContent, DetailsHeader } from "components/app_layout/DetailsLayout";
import UserInitials from "components/comments/UserInitials";
import Button from "components/ui/button/Button";
import DropdownMenu from "components/ui/dropdown_menu/DropdownMenu";
import DropdownMenuItem from "components/ui/dropdown_menu/DropdownMenuItem";
import EmptyState from "components/ui/empty_state/EmptyState";
import ConfirmModal from "components/ui/modal/ConfirmModal";
import Tabs from "components/ui/tabs/Tabs";
import toast from "components/ui/toast/Toast";
import UserName from "components/users/UserName";
import { numberSort, stringSort } from "lib/columnSort";
import { EllipsisTooltipRouterLink } from "lib/core_components/EllipsisTooltip";
import { ALWAYS_SHOW, SHOW_ON_HOVER } from "lib/core_components/ShowOnHover";
import SkeletonTable from "lib/core_components/SkeletonTable";
import { Table } from "lib/core_components/Table";
import { ColumnType } from "lib/core_components/Table/ColumnTypes";
import { BuyerList, useBuyerLists } from "lib/hooks/api/buyer_lists/useBuyerLists";
import { useDeleteBuyerList } from "lib/hooks/api/buyer_lists/useDeleteBuyerList";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useUsers } from "lib/hooks/api/useUsers";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { useDialogManager } from "lib/providers/DialogManager";
import { SortState } from "lib/search/types";
import { EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { escapeRegExp } from "lib/utils";
import BuyerListPermissionsModal, {
  BuyerListPermissionsModalProps,
} from "./BuyerListPermissionsModal";
import { UserCreateBuyerListModal } from "./CreateBuyerListModal";
import { RenameBuyerListModal, RenameBuyerListModalProps } from "./RenameBuyerListModal";

type Tab = "allBuyerLists" | "myBuyerLists";

type Actions = Record<string, { action: () => void; permission: string; label: string }>;

function NameCell({ list, onDeleteRequest }: { list: BuyerList; onDeleteRequest: () => void }) {
  const dialogManager = useDialogManager();
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const actions: Actions = {
    rename: {
      label: "Rename",
      permission: "can_update",
      action: () =>
        dialogManager.openDialog(RenameBuyerListModal, {
          list: list,
        } as Omit<RenameBuyerListModalProps, "isOpen" | "onClose">),
    },
    delete: {
      label: "Delete",
      permission: "can_delete",
      action: () => onDeleteRequest(),
    },
    permissions: {
      label: "Share permissions",
      permission: "can_update",
      action: () =>
        dialogManager.openDialog(BuyerListPermissionsModal, {
          list: list,
        } as Omit<BuyerListPermissionsModalProps, "isOpen" | "onClose">),
    },
  };

  const availableActions = Object.keys(actions).filter((key) =>
    list.permissionActions?.includes(actions[key].permission),
  );

  return (
    <NameCellContainer alignItems="center" justifyContent="space-between" gap="8px">
      <EllipsisTooltipRouterLink
        fullText={list.name}
        linkText={<b>{list.name}</b>}
        linkProps={{ to: `/buyers/lists/${list.id}` }}
      />
      {availableActions.length > 0 && (
        <DropdownWrapper className={dropdownOpen ? ALWAYS_SHOW : SHOW_ON_HOVER}>
          <DropdownMenu
            trigger={
              <Button buttonType="secondary">
                <ActionsText>Actions</ActionsText>
                <UIcon icon="chevronDown" size={12} />
              </Button>
            }
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
          >
            {availableActions.map((key) => (
              <DropdownMenuItem key={key} onClick={() => actions[key].action()}>
                {actions[key].label}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </DropdownWrapper>
      )}
    </NameCellContainer>
  );
}

function BuyerLists() {
  const { data: lists, isLoading, isFetching, isError } = useBuyerLists();
  const { data: currentUser } = useCurrentUser();
  // includeInactive: true so a now-deactivated owner still resolves in the owner column.
  const { data: users } = useUsers({ includeInactive: true });
  const [searchValue, setSearchValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState<BuyerList | null>(null);
  const { logEvent } = useTracking();

  const { mutate: deleteList } = useDeleteBuyerList({
    onSuccess: () => toast.success("List successfully deleted!"),
    onError: () => toast.error("Sorry, an error has occurred while deleting this list"),
  });

  const [tab, setTab] = useState<Tab>("allBuyerLists");

  const [sortState, setSortState] = useState<SortState>({
    field: "name",
    order: "ASC",
  });

  const getSortOrder = (
    sort: SortState,
    key: keyof BuyerList,
  ): "ascend" | "descend" | undefined => {
    if (sort.field === key) {
      return sort.order === "ASC" ? "ascend" : "descend";
    } else return undefined;
  };

  const onTabChange = useCallback(
    (key: Tab) => {
      logEvent(EventNames.tabChanged, {
        "Tab selected": key === "allBuyerLists" ? "All buyer lists" : "My buyer lists",
      });
      setTab(key);
    },
    [logEvent],
  );

  const handleChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Partial<Record<keyof BuyerList, string[]>>,
      sorter: SorterResult<BuyerList> | SorterResult<BuyerList>[],
    ) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      setSortState({
        field: (s.field as string) ?? "name",
        order: s.order === "ascend" ? "ASC" : "DESC",
      });
    },
    [],
  );

  const myBuyerLists = useMemo(() => {
    return lists?.all.filter((list) => list.ownerId === currentUser?.guid);
  }, [lists?.all, currentUser?.guid]);

  const displayedLists: BuyerList[] = useMemo(() => {
    const selectedLists = tab === "allBuyerLists" ? lists?.all : myBuyerLists;
    return (
      selectedLists?.filter((list) =>
        list.name.match(new RegExp(escapeRegExp(searchValue), "i")),
      ) ?? []
    );
  }, [lists?.all, myBuyerLists, searchValue, tab]);

  const columns: ColumnType<BuyerList>[] = useMemo(
    () => [
      {
        title: "List name",
        key: "name",
        dataIndex: "name",
        width: 440,
        render: (_, list: BuyerList) => (
          <NameCell list={list} onDeleteRequest={() => setListToDelete(list)} />
        ),
        sorter: stringSort((list) => list.name),
        sortOrder: getSortOrder(sortState, "name"),
        sortDirections: ["descend", "ascend", "descend"],
        ellipsis: true,
      },
      {
        title: "Buyers",
        key: "entries",
        dataIndex: "entries",
        render: (entries: BuyerList["entries"]) => entries.length,
        sorter: numberSort((list) => list.entries.length, getSortOrder(sortState, "entries")),
        sortDirections: ["descend", "ascend", "descend"],
        sortOrder: getSortOrder(sortState, "entries"),
      },
      {
        key: "type",
        title: "Type",
        dataIndex: "sharingType",
        sorter: stringSort((list) => list.sharingType),
        sortOrder: getSortOrder(sortState, "sharingType"),
        sortDirections: ["descend", "ascend", "descend"],
        render: (data: string) => capitalize(data),
      },
      {
        key: "owner",
        title: "Owner",
        dataIndex: "ownerId",
        sorter: stringSort((list) => {
          const user = users?.find((user) => user.guid === list.ownerId);
          return user ? `${user.firstName} ${user.lastName}` : "Admin created view";
        }),
        sortOrder: getSortOrder(sortState, "ownerId"),
        sortDirections: ["ascend", "descend", "ascend"],
        render: (owner: string) => {
          const user = users?.find((user) => user.guid === owner);
          return user ? (
            <OwnerCell alignItems="center" gap="8px">
              <UserInitials firstName={user?.firstName} lastName={user?.lastName} />
              <UserName user={user} />
            </OwnerCell>
          ) : (
            <OwnerCell alignItems="center" gap="8px">
              <UIcon icon="user01" size={24} />
              Admin created view
            </OwnerCell>
          );
        },
      },
    ],
    [sortState, users],
  );

  const dataExists = useMemo(
    (): boolean => (lists && lists.all.length > 0) || isLoading,
    [isLoading, lists],
  );

  const displayedTable = useMemo(() => {
    if (isLoading) {
      return <SkeletonTable columns={columns} pageSize={10} active />;
    }

    return (
      <TrackingProvider data={{ "Context source": "In-row" }}>
        <Table
          columns={columns}
          onChange={handleChange}
          loading={isFetching}
          isError={isError}
          dataSource={displayedLists}
          rowKey={(r) => r.id}
          locale={{
            emptyText: (
              <EmptyState
                type="noResults"
                heading="No buyer lists created yet"
                yAxisMargin="10vh"
                width="100%"
              />
            ),
          }}
          pagination={displayedLists.length < 20 ? false : undefined}
          onRow={(record) => ({
            onClick: () => {
              logEvent(EventNames.buyerListClicked, {
                "List type": "Buyer list",
                "List name": record.name,
                "List ID": record.id,
              });
            },
          })}
        />
      </TrackingProvider>
    );
  }, [columns, displayedLists, handleChange, isError, isFetching, isLoading, logEvent]);

  return (
    <>
      <UserCreateBuyerListModal
        open={isModalOpen}
        onOpenChange={(open) => setIsModalOpen(open)}
        buyerGuids={[]}
        contextSource="buyer lists page"
      />
      <BuyerListHeader>
        <TitleAndBtn alignItems="center" justifyContent="space-between">
          <Text h1>Buyer lists</Text>
          <Button onClick={() => setIsModalOpen(true)} icon="plus">
            Create list
          </Button>
        </TitleAndBtn>
        {dataExists ? (
          <Tabs
            value={tab}
            onChange={(key) => onTabChange(key as Tab)}
            padding="0"
            hasBottomBorder={false}
            height="auto"
            items={[
              {
                key: "allBuyerLists",
                label: `All buyer lists${lists?.all.length !== undefined ? ` (${lists.all.length})` : ""}`,
                children: <></>,
              },
              {
                key: "myBuyerLists",
                label: `My buyer lists${myBuyerLists?.length !== undefined ? ` (${myBuyerLists.length})` : ""}`,
                children: <></>,
              },
            ]}
          />
        ) : (
          <BottomSpacer />
        )}
      </BuyerListHeader>

      <SearchContainer>
        {dataExists && (
          <SearchInputWrapper>
            <UIcon icon="searchSm" size={16} />
            <SearchInput
              placeholder="Search lists"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            {searchValue && (
              <ClearButton onClick={() => setSearchValue("")} aria-label="Clear search">
                <UIcon icon="xClose" size={14} />
              </ClearButton>
            )}
          </SearchInputWrapper>
        )}
      </SearchContainer>
      <DetailsContent>{displayedTable}</DetailsContent>
      <ConfirmModal
        open={listToDelete !== null}
        onOpenChange={(open) => !open && setListToDelete(null)}
        onConfirm={() => listToDelete && deleteList({ id: listToDelete.id })}
        message="Are you sure you want to delete this buyer list?"
        confirmLabel="Delete"
      />
    </>
  );
}

export default BuyerLists;

// Styled components

const BuyerListHeader = styled(DetailsHeader)({
  paddingBottom: 0,
});

const TitleAndBtn = styled(Flex)({
  marginBottom: "8px",
});

const BottomSpacer = styled.span({
  paddingBottom: "16px",
});

const SearchContainer = styled.div({
  margin: "16px 24px 0",
});

const SearchInputWrapper = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  gap: "8px",
  width: "320px",
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: "4px",
  padding: "4px 8px",
  backgroundColor: theme.colors.sysBackgroundDefault,
  "&:focus-within": {
    borderColor: theme.colors.sysPrimaryDefault,
    outline: `2px solid ${theme.colors.sysPrimaryDefault}1A`,
  },
}));

const ClearButton = styled.button(({ theme }) => ({
  all: "unset",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  color: theme.colors.sysTextInactive,
  "&:hover": {
    color: theme.colors.sysTextDefault,
  },
}));

const SearchInput = styled.input(({ theme }) => ({
  flex: 1,
  border: "none",
  outline: "none",
  fontSize: "14px",
  lineHeight: "157%",
  backgroundColor: "transparent",
  color: theme.colors.sysTextBlack,
  padding: "1px 0",
  "&::placeholder": {
    color: theme.colors.sysTextInactive,
  },
}));

const NameCellContainer = styled(Flex)({
  lineHeight: "32px",
});

const ActionsText = styled.span({
  paddingRight: "4px",
});

const DropdownWrapper = styled.span({
  display: "inline-flex",
});

const OwnerCell = styled(Flex)({
  minWidth: "160px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});
